import React from 'react'
import { StatCards, Skeleton } from '@/shared/ui'
import { TrendingUp, AlertTriangle, CreditCard, Receipt, Users, Wallet, CalendarClock } from 'lucide-react'
import { getFinanceMonthlyRevenueEstimate, getFinanceMonthlySummary, listFinanceMonthlyTimeseries, type FinanceMonthlySummary, type FinanceMonthlyTimeseriesRow } from '../services/financeReportsService'
import { formatMoneyFromCents } from '../utils/money'
import { FinanceTimeseriesTable } from './FinanceTimeseriesTable'
import { FinancePlansRevenueCards } from './FinancePlansRevenueCards'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export function FinanceSummaryTab({ monthStartDate, refreshKey }: Props) {
  const [summary, setSummary] = React.useState<FinanceMonthlySummary | null>(null)
  const [series, setSeries] = React.useState<FinanceMonthlyTimeseriesRow[]>([])
  const [estimateCents, setEstimateCents] = React.useState<number | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const estimatePromise = getFinanceMonthlyRevenueEstimate(monthStartDate).catch(() => null)
      const [s, ts, est] = await Promise.all([
        getFinanceMonthlySummary(monthStartDate),
        listFinanceMonthlyTimeseries(monthStartDate, 12),
        estimatePromise,
      ])
      setSummary(s)
      setSeries(ts)
      setEstimateCents(est?.estimated_revenue_cents ?? null)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar resumo financeiro')
      setSummary(null)
      setSeries([])
      setEstimateCents(null)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { load() }, [monthStartDate, refreshKey])

  if (loading && !summary) {
    return (
      <div className="space-y-4">
        <StatCards
          items={Array.from({ length: 9 }).map(() => ({ label: '—', value: <Skeleton className="h-6 w-28" /> }))}
          className="lg:grid-cols-4"
        />
        <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="text-sm text-red-400">{error}</div>
  }

  if (!summary) {
    return <div className="text-sm text-foreground/70">Sem dados.</div>
  }

  const totalCostsCents =
    Number(summary.fixed_costs_cents || 0) +
    Number(summary.variable_costs_cents || 0) +
    Number(summary.affiliate_payouts_paid_cents || 0) +
    Number(summary.affiliate_payouts_fee_cents || 0)

  const netTone: 'success' | 'warning' = summary.net_result_cents >= 0 ? 'success' : 'warning'

  const cards = [
    {
      label: 'Receita líquida (caixa)',
      value: formatMoneyFromCents(summary.revenue_net_cents, 'BRL'),
      icon: <TrendingUp className="h-5 w-5" />,
      tone: 'success' as const,
    },
    {
      label: 'Receita bruta (pagamentos)',
      value: formatMoneyFromCents(summary.revenue_gross_cents, 'BRL'),
      icon: <CreditCard className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Receita estimada (mês)',
      value: estimateCents == null ? '—' : formatMoneyFromCents(estimateCents, 'BRL'),
      icon: <CalendarClock className="h-5 w-5" />,
      tone: 'violet' as const,
    },
    {
      label: 'Ajustes (estornos/credit notes)',
      value: formatMoneyFromCents(summary.revenue_adjustments_cents, 'BRL'),
      icon: <AlertTriangle className="h-5 w-5" />,
      tone: 'warning' as const,
    },
    {
      label: 'Custos totais (mês)',
      value: formatMoneyFromCents(totalCostsCents, 'BRL'),
      icon: <Receipt className="h-5 w-5" />,
      tone: 'violet' as const,
    },
    {
      label: 'Custos fixos (recorrentes)',
      value: formatMoneyFromCents(summary.fixed_costs_cents, 'BRL'),
      icon: <Receipt className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Custos avulsos (variáveis)',
      value: formatMoneyFromCents(summary.variable_costs_cents, 'BRL'),
      icon: <Receipt className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Payouts afiliados (pago + taxa)',
      value: formatMoneyFromCents(
        Number(summary.affiliate_payouts_paid_cents || 0) + Number(summary.affiliate_payouts_fee_cents || 0),
        'BRL',
      ),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'warning' as const,
    },
    {
      label: 'Última linha (resultado do mês)',
      value: formatMoneyFromCents(summary.net_result_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: netTone,
    },
  ]

  const counts = [
    {
      label: 'Ativos (pagantes) no mês',
      value: Number(summary.active_paying_accounts_count || 0).toLocaleString('pt-BR'),
      icon: <Users className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Cancelando (agendado)',
      value: Number(summary.canceling_accounts_count || 0).toLocaleString('pt-BR'),
      icon: <AlertTriangle className="h-5 w-5" />,
      tone: 'warning' as const,
    },
    {
      label: 'Past due / unpaid',
      value: Number(summary.past_due_accounts_count || 0).toLocaleString('pt-BR'),
      icon: <AlertTriangle className="h-5 w-5" />,
      tone: 'warning' as const,
    },
    {
      label: 'Comissões pendentes',
      value: formatMoneyFromCents(summary.commissions_pending_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'violet' as const,
    },
    {
      label: 'Comissões liberadas (earned)',
      value: formatMoneyFromCents(summary.commissions_earned_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'violet' as const,
    },
  ]

  return (
    <div className="space-y-4">
      <StatCards items={cards} className="lg:grid-cols-4" />
      <StatCards items={counts} className="lg:grid-cols-5" />
      <FinancePlansRevenueCards monthStartDate={monthStartDate} refreshKey={refreshKey} />
      <FinanceTimeseriesTable rows={series} />
    </div>
  )
}
